import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import {
  Banknote,
  Bookmark,
  Briefcase,
  Check,
  ChevronDown,
  CircleCheck,
  Clock,
  ExternalLink,
  Globe2,
  Layers,
  MapPin,
  Minus,
  Sparkles,
  ThumbsDown,
  Zap,
} from 'lucide-react';

import SvgIcon from 'components/SvgIcon/SvgIcon';
import Thumbnail from 'components/Thumbnails/Thumbnail';
import UserStatusBadges from 'components/Badges/UserStatusBadges/UserStatusBadges';
import AriAvatar from 'containers/Ari/AriAvatar';
import useAuth from 'hooks/useAuth';
import { trackEvent } from 'helpers/Analytics';
import { MATCH_BAND_LABELS } from 'helpers/ariMatchBand';
import { hasAvatar } from 'helpers/HelperFunctions';
import { getDescriptionParagraphs } from 'containers/Ari/jobDescription';
import { showActionToast } from 'components/ActionToast';
import { updateBriefSavedStatus } from 'redux/modules/entities';
import { setBriefSavedInLists } from 'redux/modules/projects2';
import {
  findEntryIdByJobRef,
  removeEntry,
  saveJob,
} from 'redux/modules/jobTracker';
import { buildSaveJobBody } from './saveJobBody';
import { mapBriefToJobCardProps } from './mapBriefToJobCardProps';
import styles from './JobCard.module.scss';

const EASY_APPLY_TOOLTIP = 'Apply quickly with Twine';
const SAVE_TOOLTIP = 'Add to your job tracker';

const STATUS_TOOLTIPS = {
  priority: 'This client urgently wants to hire',
  popular: 'Pro and Premium members can apply',
  open: 'All members can apply.',
};

const STATUS_LABELS = {
  priority: 'Priority',
  popular: 'Popular',
  open: 'Open',
};

const BAND_ICONS = {
  excellent: Check,
  good: Check,
  average: Minus,
};

function CardTooltip({ id, text, children }) {
  const body = typeof document !== 'undefined' ? document.body : undefined;

  return (
    <OverlayTrigger
      placement="top"
      trigger={['hover', 'focus', 'click']}
      rootClose
      container={body}
      overlay={
        <Tooltip id={id} className={styles.cardTooltip}>
          {text}
        </Tooltip>
      }
    >
      {children}
    </OverlayTrigger>
  );
}

CardTooltip.propTypes = {
  id: PropTypes.string.isRequired,
  text: PropTypes.node.isRequired,
  children: PropTypes.element.isRequired,
};

function Chip({ icon: Icon, label, ready, onClick }) {
  if (!label) {
    return null;
  }

  const className = `${styles.chip}${ready ? ` ${styles.chipReady}` : ''}`;

  if (onClick) {
    return (
      <button
        type="button"
        className={`${styles.chipButton}${ready ? ` ${styles.chipReady}` : ''}`}
        onClick={onClick}
      >
        {Icon ? (
          <Icon
            className={styles.chipIcon}
            size={15}
            strokeWidth={1.8}
            aria-hidden="true"
          />
        ) : null}
        {label}
      </button>
    );
  }

  return (
    <span className={className}>
      {Icon ? (
        <Icon
          className={styles.chipIcon}
          size={15}
          strokeWidth={1.8}
          aria-hidden="true"
        />
      ) : null}
      {label}
    </span>
  );
}

Chip.propTypes = {
  icon: PropTypes.elementType,
  label: PropTypes.string,
  ready: PropTypes.bool,
  onClick: PropTypes.func,
};

/**
 * Public jobs board card — used on /jobs, role/country pages, Similar jobs,
 * and saved jobs. Accepts a collaboration `brief` (preferred) or pre-mapped
 * view props for tests / Ari (A1 can pass `ari` later).
 */
export default function JobCard({
  brief,
  onClickRole,
  relatedJobs,
  onUnsave,
  onCtaClick,
  // Optional pre-mapped props (tests / Ari). When `brief` is set these are
  // ignored except `ari`, `boardLabel`, `showSave`, `title`, `cta`, etc. that
  // Ari may override via explicit props below.
  source: sourceProp,
  easyApply: easyApplyProp,
  status: statusProp,
  urgent: urgentProp,
  title: titleProp,
  client: clientProp,
  chips: chipsProp,
  excerpt: excerptProp,
  closesAt: closesAtProp,
  listedAt: listedAtProp,
  timeLine: timeLineProp,
  footerMeta: footerMetaProp,
  canApply: canApplyProp,
  saved: savedProp,
  pipelineEntryId: pipelineEntryIdProp,
  ariJobId: ariJobIdProp,
  saveEntryPoint: saveEntryPointProp,
  cta: ctaProp,
  externalHostname: externalHostnameProp,
  boardLabel: boardLabelProp,
  showSave: showSaveProp,
  jobUrl: jobUrlProp,
  id: idProp,
  ari,
  onNotRelevant,
  notRelevantCollapsed = false,
  onUndoNotRelevant,
  appliedElsewhere = false,
}) {
  const dispatch = useDispatch();
  const { user } = useAuth();
  const mapped = brief ? mapBriefToJobCardProps(brief, user) : null;

  const view = mapped
    ? {
        ...mapped,
        ...(titleProp ? { title: titleProp } : null),
        ...(ctaProp ? { cta: ctaProp } : null),
        ...(footerMetaProp ? { footerMeta: footerMetaProp } : null),
        ...(timeLineProp ? { timeLine: timeLineProp } : null),
        ...(boardLabelProp !== undefined
          ? { boardLabel: boardLabelProp }
          : null),
      }
    : {
        id: idProp || null,
        jobUrl: jobUrlProp || '#',
        source: sourceProp || 'twine',
        easyApply: Boolean(easyApplyProp),
        status: statusProp || 'none',
        urgent: Boolean(urgentProp),
        title: titleProp || '',
        client: clientProp || {
          name: '',
          owner: null,
          avatar: null,
          initials: '?',
          isPrivate: false,
          location: '',
          lastActiveAt: '',
          activityLabel: null,
          show: false,
        },
        chips: chipsProp || {
          setting: '',
          budget: '',
          role: '',
          experience: '',
          workType: '',
          readyToHire: false,
        },
        excerpt: excerptProp || '',
        closesAt: closesAtProp || '',
        listedAt: listedAtProp || '',
        timeLine: timeLineProp || '',
        footerMeta: footerMetaProp || '',
        canApply: Boolean(canApplyProp),
        saved: Boolean(savedProp),
        pipelineEntryId:
          pipelineEntryIdProp != null ? pipelineEntryIdProp : null,
        ariJobId: ariJobIdProp != null ? ariJobIdProp : null,
        saveEntryPoint: saveEntryPointProp || 'job_listing',
        cta: ctaProp || { label: 'View job', href: '#', external: false },
        externalHostname: externalHostnameProp || '',
        boardLabel: boardLabelProp || null,
        ari: null,
      };

  const ariBlock = ari !== undefined ? ari : view.ari;
  const saveBody = buildSaveJobBody({
    jobId: brief?.id || null,
    ariJobId: view.ariJobId != null ? view.ariJobId : idProp,
  });
  const showSave =
    showSaveProp !== undefined ? Boolean(showSaveProp) : Boolean(saveBody);
  const resolvedPipelineEntryId =
    brief?.pipeline_entry_id != null
      ? brief.pipeline_entry_id
      : mapped && mapped.pipelineEntryId != null
      ? mapped.pipelineEntryId
      : view.pipelineEntryId != null
      ? view.pipelineEntryId
      : null;
  const storeEntryId = useSelector((state) =>
    findEntryIdByJobRef(state, {
      jobId: saveBody && saveBody.job_id,
      ariJobId: saveBody && saveBody.ari_job_id,
    })
  );
  const [isSaved, setIsSaved] = useState(view.saved);
  const [pipelineEntryId, setPipelineEntryId] = useState(
    resolvedPipelineEntryId
  );
  const [isRemoving, setIsRemoving] = useState(false);
  const removeTimerRef = useRef(null);
  const [descriptionOpen, setDescriptionOpen] = useState(false);
  const descriptionParagraphs = ariBlock?.description
    ? getDescriptionParagraphs(ariBlock.description)
    : [];
  const descriptionId = `job-card-desc-${view.id || 'x'}`;
  const entryPoint =
    saveEntryPointProp ||
    (mapped && mapped.saveEntryPoint) ||
    view.saveEntryPoint ||
    'job_listing';

  useEffect(() => {
    setIsSaved(view.saved);
  }, [view.saved]);

  // Job tracker board / modal "Yes, I applied" write entries into Redux without
  // updating Ari card props — treat store presence as saved (like Twine briefs).
  useEffect(() => {
    if (storeEntryId == null) return;
    setIsSaved(true);
    setPipelineEntryId(storeEntryId);
  }, [storeEntryId]);

  useEffect(() => {
    if (storeEntryId != null) return;
    setPipelineEntryId(resolvedPipelineEntryId);
  }, [resolvedPipelineEntryId, storeEntryId]);

  useEffect(
    () => () => {
      if (removeTimerRef.current) {
        clearTimeout(removeTimerRef.current);
        removeTimerRef.current = null;
      }
    },
    []
  );

  const stopCardNav = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const syncSavedLocally = (saved, entryId) => {
    const jobId = brief?.id;
    if (jobId == null) {
      return;
    }
    dispatch(updateBriefSavedStatus(jobId, saved, entryId));
    dispatch(setBriefSavedInLists(jobId, saved, entryId));
  };

  const handleSave = (event) => {
    stopCardNav(event);
    if (!showSave || !saveBody) {
      return;
    }
    if (!user) {
      window.location.href = '/signup';
      return;
    }

    if (isSaved) {
      const previousEntryId = pipelineEntryId || storeEntryId;
      if (previousEntryId == null) {
        return;
      }
      if (onUnsave) {
        setIsRemoving(true);
        removeTimerRef.current = setTimeout(() => {
          removeTimerRef.current = null;
          dispatch(removeEntry(previousEntryId)).then(
            () => {
              syncSavedLocally(false, null);
              onUnsave(brief?.id || previousEntryId);
            },
            () => {
              setIsRemoving(false);
              setIsSaved(true);
              setPipelineEntryId(previousEntryId);
            }
          );
        }, 500);
      } else {
        setIsSaved(false);
        setPipelineEntryId(null);
        syncSavedLocally(false, null);
        dispatch(removeEntry(previousEntryId)).then(null, () => {
          setIsSaved(true);
          setPipelineEntryId(previousEntryId);
          syncSavedLocally(true, previousEntryId);
        });
      }
      return;
    }

    setIsSaved(true);
    dispatch(saveJob(saveBody)).then(
      (result) => {
        const entryId = result && result.id != null ? result.id : null;
        if (entryId != null) {
          setPipelineEntryId(entryId);
        }
        setIsSaved(true);
        syncSavedLocally(true, entryId);
        const source =
          (result && result.source) ||
          (saveBody.job_id != null ? 'twine' : 'ari');
        trackEvent('job-tracker-job-saved', {
          source,
          entry_point: entryPoint,
        });
        showActionToast({
          message: 'Saved to your Job tracker.',
          actionLabel: 'View',
          onAction: () => {
            if (typeof window !== 'undefined') {
              window.location.assign('/job-tracker');
            }
          },
        });
        return result;
      },
      () => {
        setIsSaved(false);
        setPipelineEntryId(null);
        syncSavedLocally(false, null);
      }
    );
  };

  const toggleDescription = (event) => {
    stopCardNav(event);
    const opening = !descriptionOpen;
    setDescriptionOpen(opening);
    if (opening) {
      trackEvent('ari-job-description-expanded', {
        internal: view.source !== 'external',
      });
    }
  };

  const handleRoleChip = (event) => {
    stopCardNav(event);
    if (!relatedJobs && onClickRole && view.chips.role) {
      onClickRole({ name: brief?.role || view.chips.role });
    }
  };

  const isExternalSource =
    view.source === 'external' || Boolean(view.boardLabel);
  const sourceLabel = view.boardLabel
    ? view.boardLabel
    : view.source === 'external'
    ? 'External'
    : view.easyApply || view.source === 'easyApply'
    ? 'Easy apply'
    : null;

  const externalTooltip = view.externalHostname
    ? `Found on ${view.externalHostname}. You apply on their site.`
    : 'You apply on their site.';

  const statusLabel = STATUS_LABELS[view.status] || null;
  const statusTooltip = STATUS_TOOLTIPS[view.status] || null;
  const statusClass =
    view.status === 'priority'
      ? styles.statusPriority
      : view.status === 'popular'
      ? styles.statusPopular
      : styles.statusOpen;

  const bandLabel = ariBlock?.band ? MATCH_BAND_LABELS[ariBlock.band] : null;
  const BandIcon = ariBlock?.band ? BAND_ICONS[ariBlock.band] : null;
  const bandClass =
    ariBlock?.band === 'excellent'
      ? styles.matchBandExcellent
      : ariBlock?.band === 'good'
      ? styles.matchBandGood
      : styles.matchBandAverage;

  const showClient = Boolean(view.client?.show && view.client?.name);
  const owner = view.client?.owner;
  const isPrivateClient = Boolean(view.client?.isPrivate);
  let hasAvatarImage = false;
  try {
    // Same gate as BriefDetail ClientCard: real photo, not a placeholder.
    hasAvatarImage = !isPrivateClient && owner && hasAvatar(owner);
  } catch (e) {
    hasAvatarImage = false;
  }

  const cardClass = `${styles.card}${isRemoving ? ` ${styles.removing}` : ''}`;
  const jobLinkExternalProps = view.cta?.external
    ? { target: '_blank', rel: 'noopener noreferrer' }
    : {};

  const handleCtaClick = (event) => {
    event.stopPropagation();
    if (onCtaClick) {
      onCtaClick(event);
    }
  };

  const handleHitAreaClick = (event) => {
    if (view.cta?.external && onCtaClick) {
      onCtaClick(event);
    }
  };

  return (
    <article className={cardClass} data-job-id={view.id || undefined}>
      {view.jobUrl ? (
        <a
          href={view.jobUrl}
          className={styles.hitArea}
          tabIndex={-1}
          aria-hidden="true"
          onClick={handleHitAreaClick}
          {...jobLinkExternalProps}
        />
      ) : null}

      <div className={styles.content}>
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            {isExternalSource && sourceLabel ? (
              <CardTooltip
                id={`job-source-${view.id || 'x'}`}
                text={externalTooltip}
              >
                <button
                  type="button"
                  className={`${styles.sourceLabel} ${styles.sourceLabelExternal}`}
                  aria-describedby={`job-source-desc-${view.id || 'x'}`}
                >
                  <ExternalLink
                    className={styles.sourceIcon}
                    size={12}
                    strokeWidth={2.4}
                    aria-hidden="true"
                  />
                  {sourceLabel}
                </button>
              </CardTooltip>
            ) : sourceLabel ? (
              <CardTooltip
                id={`job-easy-apply-${view.id || 'x'}`}
                text={EASY_APPLY_TOOLTIP}
              >
                <button
                  type="button"
                  className={`${styles.sourceLabel} ${styles.sourceLabelEasy}`}
                  aria-describedby={`job-easy-apply-desc-${view.id || 'x'}`}
                >
                  <Zap
                    className={styles.sourceIcon}
                    size={12}
                    strokeWidth={2.4}
                    aria-hidden="true"
                  />
                  {sourceLabel}
                </button>
              </CardTooltip>
            ) : null}
            {sourceLabel && !isExternalSource ? (
              <span
                id={`job-easy-apply-desc-${view.id || 'x'}`}
                className={styles.srOnly}
              >
                {EASY_APPLY_TOOLTIP}
              </span>
            ) : null}
            {isExternalSource && sourceLabel ? (
              <span
                id={`job-source-desc-${view.id || 'x'}`}
                className={styles.srOnly}
              >
                {externalTooltip}
              </span>
            ) : null}
            {view.timeLine ? (
              <span className={styles.timeLine}>{view.timeLine}</span>
            ) : null}
          </div>
          <div className={styles.headerRight}>
            {statusLabel ? (
              <CardTooltip
                id={`job-status-${view.id || 'x'}`}
                text={statusTooltip || statusLabel}
              >
                <button
                  type="button"
                  className={`${styles.statusBadge} ${statusClass}`}
                  aria-describedby={`job-status-desc-${view.id || 'x'}`}
                >
                  {view.status === 'priority' ? (
                    <Zap
                      className={styles.statusBadgeIcon}
                      size={12}
                      strokeWidth={2}
                      aria-hidden="true"
                    />
                  ) : (
                    <span className={styles.statusDot} aria-hidden="true" />
                  )}
                  {statusLabel}
                </button>
              </CardTooltip>
            ) : null}
            {statusLabel ? (
              <span
                id={`job-status-desc-${view.id || 'x'}`}
                className={styles.srOnly}
              >
                {statusTooltip || statusLabel}
              </span>
            ) : null}
            {bandLabel ? (
              <span
                className={`${styles.matchBand} ${bandClass}`}
                data-testid="job-card-match-band"
              >
                {BandIcon ? (
                  <BandIcon
                    className={styles.matchBandIcon}
                    size={13}
                    strokeWidth={2.2}
                    aria-hidden="true"
                  />
                ) : null}
                {bandLabel}
              </span>
            ) : null}
          </div>
        </div>

        <div className={styles.titleRow}>
          <a
            className={styles.title}
            href={view.jobUrl}
            {...jobLinkExternalProps}
          >
            {view.title}
          </a>
          {view.urgent ? (
            <span className={styles.urgentTag}>Urgent</span>
          ) : null}
        </div>

        {showClient ? (
          <div className={styles.clientRow}>
            {hasAvatarImage ? (
              <Thumbnail
                thumbnails={owner.avatars}
                circle
                className={styles.avatar}
              />
            ) : (
              <div
                className={`${styles.avatar} ${
                  isPrivateClient ? styles.avatarPrivate : styles.avatarInitials
                }`}
                aria-hidden="true"
              >
                {isPrivateClient ? (
                  <SvgIcon icon="user" width="13" height="18" />
                ) : (
                  view.client.initials
                )}
              </div>
            )}
            <div className={styles.clientMeta}>
              {(view.client.name || view.client.location) && (
                <span className={styles.clientNameLocation}>
                  {view.client.name ? (
                    <span className={styles.clientNameRow}>
                      <span className={styles.clientName}>
                        {view.client.name}
                      </span>
                      {!isPrivateClient && owner ? (
                        <UserStatusBadges
                          user={owner}
                          showPro={false}
                          className={styles.clientBadges}
                        />
                      ) : null}
                    </span>
                  ) : null}
                  {view.client.name && view.client.location ? (
                    <span className={styles.clientSep}> · </span>
                  ) : null}
                  {view.client.location ? (
                    <span className={styles.clientLocation}>
                      {view.client.location}
                    </span>
                  ) : null}
                </span>
              )}
              {view.client.activityLabel ? (
                <span className={styles.activity}>
                  <span className={styles.activityDot} aria-hidden="true" />
                  {view.client.activityLabel}
                </span>
              ) : null}
            </div>
          </div>
        ) : null}

        <div className={styles.chips}>
          <Chip
            icon={
              view.chips.setting === 'Remote' || !view.chips.setting
                ? Globe2
                : MapPin
            }
            label={view.chips.setting}
          />
          <Chip icon={Banknote} label={view.chips.budget} />
          <Chip
            icon={Briefcase}
            label={view.chips.role}
            onClick={
              !relatedJobs && onClickRole && view.chips.role
                ? handleRoleChip
                : undefined
            }
          />
          <Chip icon={Sparkles} label={view.chips.experience} />
          <Chip icon={Layers} label={view.chips.workType} />
          {view.chips.readyToHire ? (
            <Chip icon={CircleCheck} label="Ready to hire" ready />
          ) : null}
        </div>

        {view.excerpt ? (
          <p className={styles.excerpt}>
            {view.excerpt}{' '}
            <a
              className={styles.readMore}
              href={view.jobUrl}
              {...jobLinkExternalProps}
            >
              Read more
            </a>
          </p>
        ) : !ariBlock ? (
          <a
            className={styles.readMore}
            href={view.jobUrl}
            {...jobLinkExternalProps}
          >
            Read more
          </a>
        ) : null}

        {notRelevantCollapsed ? (
          <div
            data-testid="job-card-ari-not-relevant"
            className={styles.ariReason}
          >
            <p className={styles.ariReasonText}>
              Marked not relevant
              {onUndoNotRelevant ? (
                <>
                  {' '}
                  ·{' '}
                  <button
                    type="button"
                    className={styles.notRelevantUndo}
                    onClick={onUndoNotRelevant}
                  >
                    Undo
                  </button>
                </>
              ) : null}
            </p>
          </div>
        ) : ariBlock?.reason ? (
          <div data-testid="job-card-ari" className={styles.ariReason}>
            <AriAvatar size={24} className={styles.ariReasonAvatar} />
            <div className={styles.ariReasonContent}>
              <span className={styles.ariReasonLabel}>
                Why Ari matched this
              </span>
              <p className={styles.ariReasonText}>{ariBlock.reason}</p>
            </div>
            {onNotRelevant ? (
              <button
                type="button"
                className={styles.notRelevantButton}
                onClick={onNotRelevant}
                aria-label="Not relevant"
                data-testid="job-card-not-relevant"
              >
                <ThumbsDown size={12} strokeWidth={2.2} aria-hidden="true" />
                <span className={styles.notRelevantLabel}>Not relevant</span>
              </button>
            ) : null}
          </div>
        ) : null}
        {appliedElsewhere ? (
          <CardTooltip
            id={`job-applied-${view.id || 'x'}`}
            text="You marked this as applied outside Twine"
          >
            <span
              data-testid="job-card-applied-elsewhere"
              className={styles.appliedElsewhere}
              tabIndex={0}
            >
              <Chip icon={Check} label="Applied" ready />
            </span>
          </CardTooltip>
        ) : null}
        <div className={styles.footer}>
          <div className={styles.footerMeta}>
            {view.footerMeta ? (
              <span className={styles.footerMetaItem}>
                <Clock
                  className={styles.footerMetaIcon}
                  size={14}
                  strokeWidth={1.8}
                  aria-hidden="true"
                />
                {view.footerMeta}
              </span>
            ) : null}
          </div>
          <div className={styles.footerActions}>
            {descriptionParagraphs.length > 0 ? (
              <button
                type="button"
                className={styles.descriptionToggle}
                onClick={toggleDescription}
                aria-expanded={descriptionOpen}
                aria-controls={descriptionId}
              >
                <span className={styles.descriptionLabelFull}>
                  {descriptionOpen ? 'Hide description' : 'View description'}
                </span>
                <span className={styles.descriptionLabelShort}>
                  {descriptionOpen ? 'Hide' : 'Description'}
                </span>
                <ChevronDown
                  className={
                    descriptionOpen
                      ? styles.descriptionChevronOpen
                      : styles.descriptionChevron
                  }
                  size={14}
                  strokeWidth={2.2}
                  aria-hidden="true"
                />
              </button>
            ) : null}
            {showSave ? (
              <CardTooltip
                id={`job-save-${view.id || 'x'}`}
                text={SAVE_TOOLTIP}
              >
                <button
                  type="button"
                  className={styles.saveButton}
                  aria-pressed={isSaved}
                  aria-describedby={`job-save-desc-${view.id || 'x'}`}
                  onClick={handleSave}
                >
                  <Bookmark
                    className={styles.saveIcon}
                    width={11}
                    height={15}
                    strokeWidth={2}
                    fill={isSaved ? 'currentColor' : 'none'}
                    aria-hidden="true"
                  />
                  {isSaved ? 'Saved' : 'Save'}
                </button>
              </CardTooltip>
            ) : null}
            {showSave ? (
              <span
                id={`job-save-desc-${view.id || 'x'}`}
                className={styles.srOnly}
              >
                {SAVE_TOOLTIP}
              </span>
            ) : null}
            {view.cta.external ? (
              <a
                className={styles.ctaButton}
                href={view.cta.href}
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleCtaClick}
              >
                {view.cta.label}
                {/^View on /i.test(view.cta.label || '') ? (
                  <ExternalLink
                    className={styles.ctaIcon}
                    size={14}
                    strokeWidth={2}
                    aria-hidden="true"
                  />
                ) : null}
              </a>
            ) : (
              <a
                className={styles.ctaButton}
                href={view.cta.href}
                onClick={handleCtaClick}
              >
                {view.cta.label}
              </a>
            )}
          </div>
        </div>

        {descriptionParagraphs.length > 0 && descriptionOpen ? (
          <div className={styles.descriptionPanel} id={descriptionId}>
            <h4 className={styles.descriptionPanelTitle}>Job description</h4>
            {descriptionParagraphs.map((paragraph, index) => (
              <p
                key={`${index}-${paragraph.slice(0, 24)}`}
                className={styles.descriptionText}
              >
                {paragraph}
              </p>
            ))}
            <p className={styles.descriptionNote}>
              Extract from the{' '}
              {view.source === 'external' ? 'original post' : 'Twine job post'}{' '}
              · links removed
            </p>
          </div>
        ) : null}
      </div>
    </article>
  );
}

JobCard.propTypes = {
  brief: PropTypes.object,
  onClickRole: PropTypes.func,
  /** @deprecated Location chip is no longer clickable. */
  onClickLocation: PropTypes.func,
  relatedJobs: PropTypes.bool,
  onUnsave: PropTypes.func,
  /** Optional click handler for the primary CTA (e.g. Ari outbound tracking). */
  onCtaClick: PropTypes.func,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  jobUrl: PropTypes.string,
  source: PropTypes.oneOf([
    'twine',
    'easyApply',
    'external',
    'careers',
    'board',
  ]),
  easyApply: PropTypes.bool,
  status: PropTypes.oneOf(['priority', 'popular', 'open', 'none']),
  urgent: PropTypes.bool,
  title: PropTypes.string,
  client: PropTypes.shape({
    name: PropTypes.string,
    owner: PropTypes.object,
    avatar: PropTypes.object,
    initials: PropTypes.string,
    isPrivate: PropTypes.bool,
    location: PropTypes.string,
    lastActiveAt: PropTypes.string,
    activityLabel: PropTypes.string,
    show: PropTypes.bool,
  }),
  chips: PropTypes.shape({
    setting: PropTypes.string,
    budget: PropTypes.string,
    role: PropTypes.string,
    experience: PropTypes.string,
    workType: PropTypes.string,
    readyToHire: PropTypes.bool,
  }),
  excerpt: PropTypes.string,
  closesAt: PropTypes.string,
  listedAt: PropTypes.string,
  timeLine: PropTypes.string,
  footerMeta: PropTypes.string,
  canApply: PropTypes.bool,
  saved: PropTypes.bool,
  pipelineEntryId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  ariJobId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** Analytics entry_point for job-tracker-job-saved. */
  saveEntryPoint: PropTypes.oneOf(['job_listing', 'job_details', 'ari']),
  cta: PropTypes.shape({
    label: PropTypes.string,
    href: PropTypes.string,
    external: PropTypes.bool,
  }),
  externalHostname: PropTypes.string,
  /** Ari-only board name (e.g. LinkedIn) — does not change /jobs “External”. */
  boardLabel: PropTypes.string,
  /** When false, hide Save. Default: show when a save body can be built. */
  showSave: PropTypes.bool,
  /** Optional Ari match band + reason + expandable description. */
  ari: PropTypes.shape({
    band: PropTypes.oneOf(['excellent', 'good', 'average']),
    reason: PropTypes.string,
    description: PropTypes.string,
  }),
  /** Ari relevancy: open "What put you off?" from the reason box. */
  onNotRelevant: PropTypes.func,
  notRelevantCollapsed: PropTypes.bool,
  onUndoNotRelevant: PropTypes.func,
  appliedElsewhere: PropTypes.bool,
};
